import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from './en.json';
import es from './es.json';
import fr from './fr.json';
import de from './de.json';

const translations = { en, es, fr, de };

export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

const I18nContext = createContext({ t: (key) => key, lang: 'en', setLang: () => {} });

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

function detectBrowserLanguage() {
  const nav = navigator.language || navigator.userLanguage || 'en';
  const code = nav.split('-')[0].toLowerCase();
  return translations[code] ? code : 'en';
}

export function I18nProvider({ children, tenantLanguage }) {
  const [lang, setLangState] = useState(() => {
    // Priority: tenant setting > localStorage > browser detect
    if (tenantLanguage && translations[tenantLanguage]) return tenantLanguage;
    const stored = localStorage.getItem('lodgiflow_lang');
    if (stored && translations[stored]) return stored;
    return detectBrowserLanguage();
  });

  // Update when tenant language changes
  useEffect(() => {
    if (tenantLanguage && translations[tenantLanguage]) {
      setLangState(tenantLanguage);
    }
  }, [tenantLanguage]);

  const setLang = useCallback((code) => {
    if (translations[code]) {
      setLangState(code);
      localStorage.setItem('lodgiflow_lang', code);
    }
  }, []);

  const t = useCallback((key, fallback) => {
    const value = getNestedValue(translations[lang], key);
    if (value !== undefined) return value;
    // Fallback to English
    const enValue = getNestedValue(translations.en, key);
    if (enValue !== undefined) return enValue;
    return fallback || key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ t, lang, setLang }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  return useContext(I18nContext);
}

export default I18nProvider;
